import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, A11y, Autoplay, Thumbs } from 'swiper/modules';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';

import Container from '@common/Container';
import Section from '@common/Section';
import Row from '@common/Row';
import Col from '@common/Col';
import Img from '@common/Image';
import Heading from '@common/Heading';
import RichText from '@common/RichText';
import Button from '@common/Button';
import TitleBorder from '@common/TitleBorder';
import ImageTextOverlay from '@common/ImageTextOverlay';

// Feature icons
import ActivityPlanning from '@images/customer-stories/activity_planning.svg';
import BillingPayments from '@images/customer-stories/billing_payments.svg';
import CenterManagement from '@images/customer-stories/center_management.svg';
import DigitalCurriculum from '@images/customer-stories/digital_curriculum.svg';
import FamilyEngagement from '@images/customer-stories/family_engagement.svg';
import PhysicalCurriculum from '@images/customer-stories/physical_curriculum.svg';
import ProfDevelopment from '@images/customer-stories/prof_development.svg';

const FeatureIcons = {
  'Activity Planning': ActivityPlanning,
  'Billing & Payments': BillingPayments,
  'Center Management': CenterManagement,
  'Digital Curriculum': DigitalCurriculum,
  'Family Engagement': FamilyEngagement,
  'Physical Curriculum': PhysicalCurriculum,
  'Professional Development': ProfDevelopment
};

const Slides = ({ data }) => {
  const {
    title,
    showTitle,
    list,
    bgColour
  } = data;
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [filterBy, setFilterBy] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [filteredList, setFilteredList] = useState(list);
  const handleChange = (value) => {
    // Sets value of the dropdown
    setSelectedOption(value);

    // Filter the list
    const newList = list?.filter(slide => slide.sortBySolution === value);

    // Set the new list value
    setFilteredList(newList);
  }
  useEffect(() => {
    const newFilter = [];
    for (let i = 0; i < list?.length; i++) {
      if (newFilter.findIndex(x => x === list[i]?.sortBySolution) === -1) {
        newFilter.push(list[i]?.sortBySolution);
      }
    }
    setFilterBy(newFilter)
    handleChange(newFilter[0])
  }, [list]);
  return (
    <Section className={clsx({[`bg-${bgColour} py-5`]: bgColour })}>
      <Container>
        {(showTitle && title) && <Heading type='h2' className='mx-auto mb-4 col-lg-10 text-center'>{title}</Heading>}
        {filterBy &&
          <div className='d-flex mb-3 ms-n1' style={{overflowY: 'auto'}}>
            {filterBy.map((filter, index) => (
              <Button
                key={index}
                variant='outline-black'
                size='md'
                addClass={clsx('text-nowrap m-1', {'active':selectedOption === filter})}
                style={{borderRadius: 25}}
                copy={filter}
                onClick={() => handleChange(filter)}
              />
            ))}
          </div>
        }
        {/* Add sortby filters */}
        {filteredList &&
          <>
            <Swiper
              modules={[Navigation, A11y, Autoplay, Thumbs]} 
              slidesPerView={1}
              spaceBetween={10}
              autoplay={{delay: 8000}}
              speed={1500}
              thumbs={{ swiper: thumbsSwiper }}
              >
              {filteredList.map((slide, index) => (
                <SwiperSlide key={index}>
                  <Row>
                    <Col xs='auto' xl={9} className='mb-3 mb-xl-0'>
                      {slide.image &&
                        <ImageTextOverlay key={index} to={`/resources/customer-stories${slide.slug}`} image={slide.image} colour={slide.color} aspectRatio='2/1'>
                          <div className='text-uppercase' style={{marginBottom:'15px'}}>{slide.title}</div>
                          <div className='d-flex align-items-center fs-16 fw-normal'>
                            <span style={{marginRight: '5px'}}>{'Read story'}</span>
                            <FontAwesomeIcon icon={faChevronRight} size ='sm'/>
                          </div>
                        </ImageTextOverlay>
                      }
                    </Col>
                    <Col xl={3} as='ul' className='list-title-border ps-3'>
                      {slide.sidebar && slide.sidebar.filter(x => x.__typename === 'ContentfulTextBlock')?.slice(0,1)?.map((feature, index) => (
                        <li key={index} className={clsx('d-flex flex-column ms-1 ms-md-0 mb-3 py-2')} >
                          <div className='px-1 mb-3 position-relative'>
                            <TitleBorder/>
                            <p className='h3 mb-0'>{feature.title}</p>
                          </div>
                          <RichText p='mb-0 px-1' json={feature.text}/>
                        </li>
                      ))}
                      {slide.customer.features &&
                        <li className={clsx('d-flex flex-column justify-content-center ms-1 ms-md-0 py-2')} >
                          <div className='px-1 mb-3 position-relative'>
                            <TitleBorder/>
                            <p className='h3 mb-0'>{'Products Used'}</p>
                          </div>
                          {slide.customer.features.map((feature, index) => (
                            <Row key={index} xs='auto' className='align-items-center mb-2'>
                              <img height={26} width={26} src={FeatureIcons[feature]} alt=''/>
                              <p className='mb-0 p-0'>{feature}</p>
                            </Row>
                          ))}
                        </li>
                      }
                    </Col>
                  </Row>
                </SwiperSlide>
              ))}
              <div className='swiper-pagination d-none d-lg-block' style={{position: 'absolute', top: 0}}></div>
            </Swiper>
            <Swiper
              onSwiper={setThumbsSwiper}
              watchSlidesProgress={true}
              slidesPerView={5}
              spaceBetween={15}
              className={'thumb-slider mt-3 d-none d-lg-block'}
              >
              {filteredList.map((slide, index) => (
                <SwiperSlide key={index}>
                  <Row>
                    <Col className='p-1'>
                      <Img
                        image={slide.customer?.centerLogo?.gatsbyImageData}
                        file={slide.customer?.centerLogo?.file}
                      />
                    </Col>
                  </Row>
                </SwiperSlide>
              ))}
            </Swiper>
          </>
        }
      </Container>
    </Section>
  )
}

export default Slides
